/**
 * Bundle Selector - JavaScript
 * Version: 1.0.2
 * Gère la sélection de variantes avec quantités et calcul de prix par paliers
 */

(function() {
  'use strict';

  class BundleSelector {
    constructor(container) {
      this.container = container;
      this.selections = new Map();

      this.baseUnitPrice = parseInt(container.dataset.baseUnitPrice) || 2990;
      this.emptySelectionText = container.dataset.emptySelectionText || 'Sélectionne tes produits';
      this.tiers = this.parseTiers(container.dataset.tiers);
      this.showQuantityMeter = container.dataset.showQuantityMeter === 'true';

      this.cards = container.querySelectorAll('.bundle-card');

      if (this.showQuantityMeter) {
        this.quantityMeter = container.querySelector('.bundle-quantity-meter');
        this.progressLine = container.querySelector('[data-progress-line]');
        this.meterItems = container.querySelectorAll('.bundle-quantity-meter__item');
      }

      this.init();
    }

    parseTiers(tiersJson) {
      try {
        return JSON.parse(tiersJson || '[]');
      } catch (e) {
        console.error('Bundle Selector: Invalid tiers config', e);
        return [
          { qty: 1, text: '1 produit', price_cents: 2990 },
          { qty: 2, text: '2 produits', price_cents: 2690 },
          { qty: 3, text: '3 produits', price_cents: 2490 },
          { qty: 4, text: '4+ produits', price_cents: 2190 }
        ];
      }
    }

    init() {
      this.bindEvents();
      this.updateUI();

      if (this.showQuantityMeter) {
        window.addEventListener('resize', () => this.updateQuantityMeter());
        requestAnimationFrame(() => this.updateQuantityMeter());
      }
    }

    bindEvents() {
      this.cards.forEach(card => {
        const minusBtn = card.querySelector('[data-action="minus"]');
        const plusBtn = card.querySelector('[data-action="plus"]');

        if (minusBtn) {
          minusBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.decrementQuantity(card);
          });
        }

        if (plusBtn) {
          plusBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.incrementQuantity(card);
          });
        }
      });
    }

    getVariantId(card) {
      return card.dataset.variantId;
    }

    getQuantity(variantId) {
      return this.selections.get(variantId) || 0;
    }

    setQuantity(variantId, quantity) {
      if (quantity <= 0) {
        this.selections.delete(variantId);
      } else {
        this.selections.set(variantId, quantity);
      }
      this.updateUI();
      this.dispatchEvents();
    }

    incrementQuantity(card) {
      const variantId = this.getVariantId(card);
      const currentQty = this.getQuantity(variantId);
      const maxQty = parseInt(card.querySelector('.bundle-card__quantity-input')?.dataset.max) || 999;
      if (currentQty < maxQty) this.setQuantity(variantId, currentQty + 1);
    }

    decrementQuantity(card) {
      const variantId = this.getVariantId(card);
      const currentQty = this.getQuantity(variantId);
      if (currentQty > 0) this.setQuantity(variantId, currentQty - 1);
    }

    getTotalQuantity() {
      let total = 0;
      this.selections.forEach(qty => { total += qty; });
      return total;
    }

    getCurrentTier() {
      const totalQty = this.getTotalQuantity();
      let currentTier = this.tiers[0];
      for (const tier of this.tiers) {
        if (totalQty >= tier.qty) currentTier = tier;
      }
      return currentTier;
    }

    calculateTotalPrice() {
      return this.getTotalQuantity() * this.getCurrentTier().price_cents;
    }

    calculateBasePriceTotal() {
      return this.getTotalQuantity() * this.baseUnitPrice;
    }

    updateUI() {
      this.cards.forEach(card => {
        const variantId = this.getVariantId(card);
        const input = card.querySelector('.bundle-card__quantity-input');
        const minusBtn = card.querySelector('[data-action="minus"]');

        if (input) {
          const qty = this.getQuantity(variantId);
          input.value = qty;
          if (minusBtn) minusBtn.disabled = qty <= 0;
        }
      });

      this.updateCardPrices();
      if (this.showQuantityMeter) this.updateQuantityMeter();
    }

    updateCardPrices() {
      const freeDistribution = this.calculateFreeDistribution();

      this.cards.forEach(card => {
        const variantId = this.getVariantId(card);
        const priceEl = card.querySelector('[data-card-price]');
        if (!priceEl) return;

        const qty = this.getQuantity(variantId);
        const freeQty = freeDistribution.get(variantId) || 0;
        const paidQty = qty - freeQty;

        if (qty === 0) {
          priceEl.innerHTML = this.formatPrice(this.baseUnitPrice);
          return;
        }

        const totalPrice = qty * this.baseUnitPrice;

        if (freeQty > 0 && freeQty === qty) {
          priceEl.innerHTML = `
            <span class="bundle-card__price-free">
              <span class="bundle-card__price-old">${this.formatPrice(totalPrice)}</span>
              <span class="bundle-card__price-free-badge">Offert</span>
            </span>`;
        } else if (freeQty > 0) {
          const paidPrice = paidQty * this.baseUnitPrice;
          priceEl.innerHTML = `
            <span class="bundle-card__price-old">${this.formatPrice(totalPrice)}</span>
            <span class="bundle-card__price-new">${this.formatPrice(paidPrice)}</span>`;
        } else {
          priceEl.innerHTML = this.formatPrice(totalPrice);
        }
      });
    }

    calculateFreeDistribution() {
      const distribution = new Map();
      const headerEl = document.querySelector('[data-bundle-header]');
      if (!headerEl) return distribution;

      const enableBonus = headerEl.dataset.enableBonus === 'true';
      if (!enableBonus) return distribution;

      const bonusGetQty = parseInt(headerEl.dataset.bonusGetQty) || 3;
      const totalQty = this.getTotalQuantity();
      const totalFree = Math.floor(totalQty / bonusGetQty);
      if (totalFree === 0) return distribution;

      const variants = [];
      this.selections.forEach((qty, variantId) => {
        if (qty > 0) variants.push({ variantId, qty });
      });
      variants.sort((a, b) => a.qty - b.qty);

      let remainingFree = totalFree;
      for (const variant of variants) {
        if (remainingFree <= 0) break;
        const freeForVariant = Math.min(variant.qty, remainingFree);
        distribution.set(variant.variantId, freeForVariant);
        remainingFree -= freeForVariant;
      }

      return distribution;
    }

    formatPrice(cents) {
      return `${(cents / 100).toFixed(2).replace('.', ',')}€`;
    }

    updateQuantityMeter() {
      if (!this.showQuantityMeter || !this.quantityMeter) return;
      const totalQty = this.getTotalQuantity();
      let currentTierIndex = 0;
      for (let i = 0; i < this.tiers.length; i++) {
        if (totalQty >= this.tiers[i].qty) currentTierIndex = i;
      }

      this.meterItems.forEach((item, index) => {
        const tier = this.tiers[index];
        if (!tier) return;
        const circle = item.querySelector('.bundle-quantity-meter__item-circle');
        const circleInner = item.querySelector('.bundle-quantity-meter__item-circle-inner');
        const price = item.querySelector('.bundle-quantity-meter__item-price');
        const isFilled = totalQty >= tier.qty;
        const isActive = totalQty > 0 && index === currentTierIndex;
        if (circle) circle.classList.toggle('filled', isFilled);
        if (circleInner) circleInner.classList.toggle('filled', isFilled);
        if (price) price.classList.toggle('active', isActive);
      });

      this.updateProgressLine(totalQty, currentTierIndex);
    }

    updateProgressLine(totalQty, currentTierIndex) {
      if (!this.showQuantityMeter || !this.progressLine || !this.meterItems || this.meterItems.length < 2) return;
      const bgLine = this.quantityMeter.querySelector('.bundle-quantity-meter__line');
      const circles = Array.from(this.meterItems).map(item =>
        item.querySelector('.bundle-quantity-meter__item-circle')
      ).filter(Boolean);
      if (circles.length < 2) return;

      const containerRect = this.quantityMeter.getBoundingClientRect();
      const firstRect = circles[0].getBoundingClientRect();
      const lastRect = circles[circles.length - 1].getBoundingClientRect();
      const overhang = 7;
      const bgStartX = firstRect.left - containerRect.left - overhang;
      const bgEndX = lastRect.right - containerRect.left + overhang;
      const bgWidth = bgEndX - bgStartX;

      if (bgLine) {
        bgLine.style.left = `${bgStartX}px`;
        bgLine.style.width = `${bgWidth}px`;
        bgLine.classList.add('positioned');
      }

      if (totalQty === 0) {
        this.progressLine.style.left = `${bgStartX}px`;
        this.progressLine.style.width = '0';
      } else {
        const targetCircle = circles[Math.min(currentTierIndex, circles.length - 1)];
        const targetRect = targetCircle.getBoundingClientRect();
        const targetCenter = targetRect.left + targetRect.width / 2 - containerRect.left;
        const isLastTier = currentTierIndex === circles.length - 1;
        const filledEndX = isLastTier ? bgEndX : targetCenter;
        this.progressLine.style.left = `${bgStartX}px`;
        this.progressLine.style.width = `${filledEndX - bgStartX}px`;
      }
      this.progressLine.classList.add('positioned');
    }

    dispatchEvents() {
      const totalQty = this.getTotalQuantity();
      const totalPrice = this.calculateTotalPrice();
      const basePriceTotal = this.calculateBasePriceTotal();
      const currentTier = this.getCurrentTier();

      document.dispatchEvent(new CustomEvent('bundlePrice:change', {
        detail: {
          hasSelections: totalQty > 0,
          totalQuantity: totalQty,
          totalPrice,
          basePriceTotal,
          currentTier,
          emptyText: this.emptySelectionText,
          selections: Array.from(this.selections.entries()).map(([id, qty]) => ({ variantId: id, quantity: qty }))
        }
      }));

      document.dispatchEvent(new CustomEvent('bundleQuantityChanged', {
        detail: {
          totalQuantity: totalQty,
          selections: Array.from(this.selections.entries())
        }
      }));
    }

    getFormData() {
      const items = [];
      this.selections.forEach((quantity, variantId) => {
        if (quantity > 0) items.push({ id: parseInt(variantId), quantity });
      });
      return items;
    }
  }

  function init() {
    const containers = document.querySelectorAll('[data-bundle-selector]');
    containers.forEach(container => {
      if (!container._bundleSelector) {
        container._bundleSelector = new BundleSelector(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.BundleSelector = BundleSelector;

})();